import { AltArrowDown, AltArrowLeft, AltArrowRight, AltArrowUp } from '@solar-icons/react';
import type { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import ImagePlaceholder from '~/components/ImagePlaceholder';
import EmblaSlider from '~/components/Slider/EmblaSlider';
import { usePrevNextButtons } from '~/components/Slider/EmblaSliderArrowButtons';
import { Image, ProductLabel } from '~/types/Product';
import { getProductImages } from '~/utils/images';

interface Props {
  images: Image[] | Record<string, Image[]>;
  activeImage: string | null;
  onChange: (_newImage: string) => void;
  onOpenLightbox: () => void;
  productLabels?: ProductLabel[];
  overlayImage?: string | null;
}

const ProductGallery: React.FC<Props> = ({
  images,
  activeImage,
  onChange,
  onOpenLightbox,
  productLabels,
  overlayImage,
}) => {
  const { t } = useTranslation();
  const [mainApi, setMainApi] = useState<EmblaCarouselType | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [thumbRef, thumbApi] = useEmblaCarousel({
    axis: isMobile ? 'x' : 'y',
    dragFree: true,
    containScroll: 'keepSnaps',
    watchDrag: true,
  });

  const { prevBtnDisabled, nextBtnDisabled } = usePrevNextButtons(thumbApi ?? undefined);

  // Scroll the thumbnail rail by a full "page" of visible thumbnails per click.
  // (scrollPrev/scrollNext only advance one snap, which feels like nothing happens
  // with dragFree — and a nearest-snap settle can resolve to a sub-pixel move.)
  const scrollThumbsBy = useCallback(
    (direction: 1 | -1) => {
      if (!thumbApi) return;
      const step = Math.max(1, thumbApi.slidesInView().length - 1);
      const current = thumbApi.selectedScrollSnap();
      thumbApi.scrollTo(current + direction * step);
    },
    [thumbApi]
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 991.98px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const largeImages = useMemo(() => getProductImages(images, 'large_thumbnail'), [images]);
  const smallImages = useMemo(() => getProductImages(images, 'small_thumbnail'), [images]);

  const selectedIndex = useMemo(
    () => largeImages.findIndex((img) => img.path === activeImage),
    [largeImages, activeImage]
  );

  useEffect(() => {
    if (!mainApi) {
      return undefined;
    }

    const onSelect = () => {
      const index = mainApi.selectedScrollSnap();
      const image = largeImages[index];
      if (!image) return;

      thumbApi?.scrollTo(index);
      onChange(image.path);
    };

    mainApi.on('select', onSelect);

    return () => {
      mainApi.off('select', onSelect);
    };
  }, [mainApi, thumbApi, largeImages, onChange]);

  useEffect(() => {
    if (mainApi && selectedIndex >= 0) {
      mainApi.scrollTo(selectedIndex);
      thumbApi?.scrollTo(selectedIndex);
    }
  }, [mainApi, thumbApi, selectedIndex]);
  const handleThumbnailClick = (index: number, path: string) => {
    mainApi?.scrollTo(index);
    onChange(path);
  };

  if (!largeImages.length) {
    return <ImagePlaceholder style={{ width: '100%', height: '100%' }} />;
  }

  return (
    <div className="product-gallery mb-5">
      <div className="product-gallery__layout">
        {smallImages.length > 1 && (
          <div className="embla embla--thumbs">
            {!(prevBtnDisabled && nextBtnDisabled) && (
              <Button
                variant="link"
                className="embla--thumbs__nav embla--thumbs__nav--prev"
                onClick={() => scrollThumbsBy(-1)}
                disabled={prevBtnDisabled}
                aria-label={t('components.product.prev_image')}
              >
                {isMobile ? <AltArrowLeft weight="Linear" size={20} /> : <AltArrowUp weight="Linear" size={20} />}
              </Button>
            )}
            <div className="embla__viewport" ref={thumbRef}>
              <div className="embla__container">
                {smallImages.map((img, index) => (
                  <Button
                    key={img.id}
                    variant="link"
                    className={`embla__slide embla__slide--thumb ${index === selectedIndex ? 'active' : ''}`}
                    onClick={() => {
                      handleThumbnailClick(index, largeImages[index]?.path ?? img.path);
                    }}
                    aria-label={t('components.product.view_image', { index: index + 1 })}
                    aria-current={index === selectedIndex ? 'true' : 'false'}
                    aria-pressed={index === selectedIndex}
                  >
                    <img
                      src={img.path}
                      alt=""
                      className="w-100 h-100 object-fit-cover rounded"
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      width="134"
                      height="134"
                    />
                  </Button>
                ))}
              </div>
            </div>
            {!(prevBtnDisabled && nextBtnDisabled) && (
              <Button
                variant="link"
                className="embla--thumbs__nav embla--thumbs__nav--next"
                onClick={() => scrollThumbsBy(1)}
                disabled={nextBtnDisabled}
                aria-label={t('components.product.next_image')}
              >
                {isMobile ? <AltArrowRight weight="Linear" size={20} /> : <AltArrowDown weight="Linear" size={20} />}
              </Button>
            )}
          </div>
        )}
        <div className="position-relative product-gallery__main">
          {productLabels && productLabels.length > 0 && (
            <div className="product-miniature__badges">
              {productLabels?.map((label) => (
                <Badge
                  pill
                  className="product-miniature__badge"
                  style={{ backgroundColor: label.color, ...(label.textColor ? { color: label.textColor } : {}) }}
                  key={label.id}
                >
                  {label.iconPath && (
                    <img
                      src={label.iconPath}
                      alt={`${label.name} icon`}
                      className="me-1"
                      decoding="async"
                      width={14}
                      height={14}
                    />
                  )}
                  {label.name}
                </Badge>
              ))}
            </div>
          )}
          <EmblaSlider onApiReady={setMainApi} showProgress={false}>
            {largeImages.map((img, index) => (
              <button
                key={img.id}
                type="button"
                className="bg-light overflow-hidden product-gallery__image-btn"
                onClick={onOpenLightbox}
                aria-label={t('components.product.view_image', { index: index + 1 })}
              >
                <img
                  src={img.path}
                  alt="Product image"
                  className="img-fluid w-100 h-100 object-fit-contain rounded-3"
                  width="736"
                  height="736"
                  decoding="async"
                  {...(index === 0
                    ? {
                        fetchpriority: 'high',
                        loading: 'eager',
                      }
                    : {
                        fetchpriority: 'low',
                        loading: 'lazy',
                      })}
                />
              </button>
            ))}
          </EmblaSlider>
          {overlayImage && (
            <div className="product-gallery__customization-overlay">
              <img src={overlayImage} alt="" className="product-gallery__customization-overlay-img" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductGallery;
